import { Box, Button, Container, Flex, HStack, Icon } from "@chakra-ui/react";
import {
  FiActivity,
  FiBarChart2,
  FiGlobe,
  FiLink,
  FiUsers
} from "react-icons/fi";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import logoLight from "./assets/logo-light.svg";
import AdDetail from "./pages/AdDetail";
import BulkCreateLinks from "./pages/BulkCreateLinks";
import BulkUpdateLinks from "./pages/BulkUpdateLinks";
import Campaigns from "./pages/Campaigns";
import CampaignsTest from "./pages/CampaignsTest";
import CreateABTest from "./pages/CreateABTest";
import CreateCampaign from "./pages/CreateCampaign";
import CreateLink from "./pages/CreateLink";
import CreatePath from "./pages/CreatePath";
import CreatePreconnect from "./pages/CreatePreconnect";
import CreateScript from "./pages/CreateScript";
import Customers from "./pages/Customers";
import EditABTest from "./pages/EditABTest";
import EditCampaign from "./pages/EditCampaign";
import EditLink from "./pages/EditLink";
import Links from "./pages/Links";
import LinksTest from "./pages/LinksTest";
import Login from "./pages/Login";
import Traffic from "./pages/Traffic";
import Website from "./pages/Website";

// Navigation Bar Component
function Navbar() {
  const location = useLocation();

  // Don't show navbar on login page
  if (location.pathname === "/login") {
    return null;
  }

  const isLinksActive = location.pathname.startsWith("/links");
  const isCampaignsActive = location.pathname.startsWith("/campaigns");
  const isCustomersActive = location.pathname.startsWith("/customers");
  const isTrafficActive = location.pathname.startsWith("/traffic");
  const isWebsiteActive = location.pathname.startsWith("/website");

  return (
    <Box
      bg="white"
      shadow="sm"
      borderBottom="1px"
      borderColor="gray.200"
      position="sticky"
      top="0"
      zIndex="sticky"
      backdropFilter="blur(10px)"
      _after={{
        content: '""',
        position: "absolute",
        inset: 0,
        bg: "white",
        opacity: 0.95,
        zIndex: -1
      }}
    >
      <Container maxW="7xl">
        <Flex h="16" alignItems="center" justifyContent="space-between">
          {/* Logo/Brand */}
          <Flex alignItems="center">
            <img
              src={logoLight}
              alt="CTP Logo"
              style={{ width: "84px", height: "auto" }}
            />
          </Flex>

          {/* Navigation Items */}
          <HStack gap={1}>
            <Link to="/links">
              <Button
                variant={isLinksActive ? "solid" : "ghost"}
                colorScheme={isLinksActive ? "blue" : "gray"}
                size="md"
                px={5}
                rounded="lg"
                fontWeight={isLinksActive ? "semibold" : "medium"}
                _hover={{
                  transform: "translateY(-1px)",
                  bg: isLinksActive ? "blue.600" : "gray.100"
                }}
                transition="all 0.2s"
              >
                <HStack gap={2}>
                  <Icon as={FiLink} boxSize={5} />
                  <span>Links</span>
                </HStack>
              </Button>
            </Link>

            <Link to="/campaigns">
              <Button
                variant={isCampaignsActive ? "solid" : "ghost"}
                colorScheme={isCampaignsActive ? "blue" : "gray"}
                size="md"
                px={5}
                rounded="lg"
                fontWeight={isCampaignsActive ? "semibold" : "medium"}
                _hover={{
                  transform: "translateY(-1px)",
                  bg: isCampaignsActive ? "blue.600" : "gray.100"
                }}
                transition="all 0.2s"
              >
                <HStack gap={2}>
                  <Icon as={FiBarChart2} boxSize={5} />
                  <span>Campaigns</span>
                </HStack>
              </Button>
            </Link>

            <Link to="/customers">
              <Button
                variant={isCustomersActive ? "solid" : "ghost"}
                colorScheme={isCustomersActive ? "blue" : "gray"}
                size="md"
                px={5}
                rounded="lg"
                fontWeight={isCustomersActive ? "semibold" : "medium"}
                _hover={{
                  transform: "translateY(-1px)",
                  bg: isCustomersActive ? "blue.600" : "gray.100"
                }}
                transition="all 0.2s"
              >
                <HStack gap={2}>
                  <Icon as={FiUsers} boxSize={5} />
                  <span>Customers</span>
                </HStack>
              </Button>
            </Link>

            <Link to="/traffic">
              <Button
                variant={isTrafficActive ? "solid" : "ghost"}
                colorScheme={isTrafficActive ? "blue" : "gray"}
                size="md"
                px={5}
                rounded="lg"
                fontWeight={isTrafficActive ? "semibold" : "medium"}
                _hover={{
                  transform: "translateY(-1px)",
                  bg: isTrafficActive ? "blue.600" : "gray.100"
                }}
                transition="all 0.2s"
              >
                <HStack gap={2}>
                  <Icon as={FiActivity} boxSize={5} />
                  <span>Traffic</span>
                </HStack>
              </Button>
            </Link>

            <Link to="/website">
              <Button
                variant={isWebsiteActive ? "solid" : "ghost"}
                colorScheme={isWebsiteActive ? "blue" : "gray"}
                size="md"
                px={5}
                rounded="lg"
                fontWeight={isWebsiteActive ? "semibold" : "medium"}
                _hover={{
                  transform: "translateY(-1px)",
                  bg: isWebsiteActive ? "blue.600" : "gray.100"
                }}
                transition="all 0.2s"
              >
                <HStack gap={2}>
                  <Icon as={FiGlobe} boxSize={5} />
                  <span>Website</span>
                </HStack>
              </Button>
            </Link>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
}

function App() {
  return (
    <Box minH="100vh" bg="gray.50">
      <Navbar />
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/links" element={<Links />} />
        <Route path="/links-test" element={<LinksTest />} />
        <Route path="/links/create" element={<CreateLink />} />
        <Route path="/links/bulk-create" element={<BulkCreateLinks />} />
        <Route path="/links/bulk-update" element={<BulkUpdateLinks />} />
        <Route path="/links/:id/edit" element={<EditLink />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/campaigns-test" element={<CampaignsTest />} />
        <Route path="/campaigns/create" element={<CreateCampaign />} />
        <Route path="/campaigns/:id/edit" element={<EditCampaign />} />
        <Route
          path="/campaigns/:campaignId/adsets/:adsetId/ads/:adId"
          element={<AdDetail />}
        />
        <Route path="/customers" element={<Customers />} />
        <Route path="/traffic" element={<Traffic />} />
        <Route path="/website" element={<Website />} />
        <Route path="/website/ab-test/create" element={<CreateABTest />} />
        <Route path="/website/ab-test/:id/edit" element={<EditABTest />} />
        <Route path="/website/path/create" element={<CreatePath />} />
        <Route
          path="/website/preconnect/create"
          element={<CreatePreconnect />}
        />
        <Route path="/website/script/create" element={<CreateScript />} />
        <Route path="/website/script/:id" element={<CreateScript />} />
      </Routes>
    </Box>
  );
}

export default App;
